import { useState } from 'react';
import { api } from '../api';
import { useApi } from '../lib/useApi';
import { Card, Empty, ErrorState, Loading, PlanBadge, Usage } from '../components/ui';
import { fmtDate, gb, gbBytes, hrs, n0, plural } from '../lib/format';
import { loadPlans, STATUS } from '../lib/plans';
import WorkspaceDrawer from './WorkspaceDrawer';
import UserDrawer from './UserDrawer';

const FILTERS = [
  ['all', 'все'], ['ACTIVE', 'платят'], ['TRIALING', 'триал'], ['UNPAID', 'не оплачено'],
  ['EXPIRED', 'истекли'], ['RETENTION', 'на удалении'], ['full', 'место >80%'], ['soon', 'триал ≤7 дней'],
];
const COLUMNS = [
  ['title', 'пространство', ''], [null, 'подписка', ''], ['members', 'люди', 'num'],
  ['storage', 'место', 'num'], ['projects', 'проекты', 'num'], ['videos', 'видео', 'num'],
  ['comments', 'коммент.', 'num'], ['revenue', '₽/мес', 'num'], ['activity', 'активность', 'num'],
];
const use = (o, code) => (o.usage || []).find((u) => u.code === code) || { used: 0, max: 0 };

export default function Spaces({ nonce }) {
  const [status, setStatus] = useState('all');
  const [planId, setPlanId] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'storage', dir: -1 });
  const [openId, setOpenId] = useState(null);
  const [openUser, setOpenUser] = useState(null);

  const plans = useApi(() => loadPlans(api), []);
  const { data, loading, error, reload } = useApi(
    () => api.workspaces({ status, planId, q, sort: sort.key, dir: sort.dir }),
    [status, planId, q, sort.key, sort.dir, nonce]
  );

  const sortBy = (key) => {
    if (!key) return;
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === 'title' ? 1 : -1 }));
  };

  return (
    <>
      <div className="filters">
        {FILTERS.map(([k, label]) => (
          <button key={k} className={`chip${status === k ? ' on' : ''}`} onClick={() => setStatus(k)}>{label}</button>
        ))}
        <input className="search" placeholder="название или почта владельца" value={q} onChange={(e) => setQ(e.target.value)} />
        {data ? (
          <span className="count">
            {n0(data.totals.workspaces)} РП · {n0(data.totals.people)}{' '}
            {plural(data.totals.people, 'человек', 'человека', 'человек')} · {gbBytes(data.totals.sizeBytes)}
          </span>
        ) : null}
      </div>

      <div className="filters" style={{ marginTop: -4 }}>
        <span className="muted" style={{ fontSize: 12 }}>тариф:</span>
        <button className={`chip${planId === 'all' ? ' on' : ''}`} onClick={() => setPlanId('all')}>любой</button>
        {(plans.data || []).map((p) => (
          <button key={p.id} className={`chip${String(planId) === String(p.id) ? ' on' : ''}`}
            onClick={() => setPlanId(p.id)} title={p.onSite === false ? 'нет на витрине сайта' : undefined}>
            {p.name}{p.onSite === false ? ' ' : ''}
            {p.onSite === false ? <span className="muted">· скрытый</span> : null}
          </button>
        ))}
      </div>

      {loading ? <Loading what="пространства" />
        : error ? <ErrorState error={error} onRetry={reload} />
        : (
          <Card className="tablecard">
            <table>
              <thead>
                <tr>
                  {COLUMNS.map(([key, label, cls]) => (
                    <th key={label} className={`${cls}${key ? ' s' : ''}${sort.key === key ? ' sorted' : ''}`}
                      onClick={() => sortBy(key)}>
                      {label}<span className="ar">{sort.dir < 0 ? '↓' : '↑'}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((o) => {
                  const status = o.retention ? 'RETENTION' : o.subscription?.status;
                  const st = STATUS[status] || { ru: status || 'нет подписки', cls: 'b-expired' };
                  const seats = use(o, 'AVAILABLE_USER_COUNT');
                  const space = use(o, 'AVAILABLE_SPACE_MB');
                  const projects = use(o, 'AVAILABLE_PROJECT_COUNT');
                  const sub = o.retention ? 'удаление ' + fmtDate(o.retention.deadlineAt)
                    : o.subscription.status === 'TRIALING' && o.subscription.trialEndDate
                      ? 'до ' + fmtDate(o.subscription.trialEndDate)
                      : o.subscription.endDate
                        ? (o.subscription.annual ? 'год, ' : '') + 'продление ' + fmtDate(o.subscription.endDate)
                        : '';
                  return (
                    <tr key={o.id} onClick={() => setOpenId(o.id)}>
                      <td><div className="t-name">{o.title}</div><div className="t-sub">{o.owners[0]}</div></td>
                      <td>
                        <PlanBadge plan={o.plan?.name || '—'} published={o.plan ? o.plan.published : true} />{' '}
                        <span className={`badge ${st.cls}`}>{st.ru}</span>
                        <div className="t-sub">{sub}</div>
                      </td>
                      <td className="num"><Usage used={o.members?.active ?? seats.used} max={seats.max} /></td>
                      <td className="num"><Usage used={space.used} max={space.max} fmt={gb} updating={space.updating} /></td>
                      <td className="num">{n0(o.projects)} <span className="muted" style={{ fontSize: 11 }}>/ {projects.max || '∞'}</span></td>
                      <td className="num">{n0(o.videos)}<div className="t-sub">{hrs(o.durationSec)}</div></td>
                      <td className="num">{n0(o.comments)}</td>
                      <td className="num">{o.monthlyRevenue ? n0(o.monthlyRevenue) : <span className="muted">—</span>}</td>
                      <td className="num muted" style={{ fontSize: 12 }}>{fmtDate(o.lastActivityAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data.items.length ? null : <Empty>под эти условия ничего не подошло</Empty>}
          </Card>
        )}

      <WorkspaceDrawer id={openId} onClose={() => setOpenId(null)} onOpenUser={(uid) => { setOpenId(null); setOpenUser(uid); }} />
      <UserDrawer id={openUser} onClose={() => setOpenUser(null)} />
    </>
  );
}
